import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  createNotificationRule,
  deleteNotificationRule,
  getNotificationRules,
  toggleNotificationRule,
  updateNotificationRule,
} from 'features/newsales/newsalesActions';
import {
  selectNotifications,
  selectNotificationsDeleteSuccess,
  selectNotificationsLoading,
  selectNotificationsSuccess,
  selectNotificationsToggleSuccess,
  selectNotificationsUpdateSuccess,
  setNotificationsCreateSuccess,
  setNotificationsDeleteSuccess,
  setNotificationsToggleSuccess,
  setNotificationsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import NotificationCard from '../components/NotificationCard';
import NotificationModal from '../components/NotificationModal';
import SearchFilter from '../components/SearchFilter';
import {
  CardsGrid,
  EmptyState,
  LoadingSpinner,
  NotificationsContainer,
  NotificationsContent,
  NotificationsTitle,
} from './Notifications.styles';

const Notifications = () => {
  const dispatch = useDispatch();

  const notifications = useSelector(selectNotifications);
  const loading = useSelector(selectNotificationsLoading);
  const createSuccess = useSelector(selectNotificationsSuccess);
  const updateSuccess = useSelector(selectNotificationsUpdateSuccess);
  const deleteSuccess = useSelector(selectNotificationsDeleteSuccess);
  const toggleSuccess = useSelector(selectNotificationsToggleSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    dispatch(getNotificationRules());
  }, [dispatch]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setSelectedNotification(null);
      dispatch(setNotificationsCreateSuccess(false));
      dispatch(getNotificationRules());
    }
  }, [createSuccess, dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setSelectedNotification(null);
      dispatch(setNotificationsUpdateSuccess(false));
      dispatch(getNotificationRules());
    }
  }, [updateSuccess, dispatch]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setNotificationsDeleteSuccess(false));
      dispatch(getNotificationRules());
    }
  }, [deleteSuccess, dispatch]);

  useEffect(() => {
    if (toggleSuccess) {
      dispatch(setNotificationsToggleSuccess(false));
      dispatch(getNotificationRules());
    }
  }, [toggleSuccess, dispatch]);

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedNotification(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const found = notifications?.find((n) => n.id === id);
    if (found) {
      setModalMode('edit');
      setSelectedNotification(found);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };

  const handleSubmit = (idOrData, data) => {
    if (modalMode === 'create') {
      dispatch(createNotificationRule(idOrData));
    } else {
      dispatch(updateNotificationRule({ id: idOrData, data }));
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteNotificationRule(id));
  };

  const handleToggle = (id) => {
    dispatch(toggleNotificationRule(id));
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
  };

  const filteredNotifications = notifications?.filter((notification) => {
    const matchesSearch = searchValue
      ? notification.name?.toLowerCase().includes(searchValue.toLowerCase())
      : true;

    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'enabled'
          ? notification.enabled === true
          : notification.enabled === false;

    return matchesSearch && matchesStatus;
  });

  return (
    <NotificationsContainer>
      <NotificationsTitle>Notifications</NotificationsTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search notifications..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        createButtonText="+ Create Notification"
      />

      <NotificationsContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !filteredNotifications || filteredNotifications.length === 0 ? (
          <EmptyState>No notifications found</EmptyState>
        ) : (
          <CardsGrid>
            {filteredNotifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onToggle={handleToggle}
                onEdit={handleOpenEditModal}
                onDelete={handleDelete}
              />
            ))}
          </CardsGrid>
        )}
      </NotificationsContent>

      <NotificationModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedNotification}
      />
    </NotificationsContainer>
  );
};

export default Notifications;
